/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { useForm, FormProvider } from 'react-hook-form';
import {
    Autocomplete,
    Avatar,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grow,
    IconButton,
    InputAdornment,
    LinearProgress,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DataGridPro } from '@mui/x-data-grid-pro';
import Iconify from 'src/components/Iconify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import SelectMultiTreatments from 'src/components/Dropdowns/SelectMultiTreatments';
import { callApi } from 'src/utils/apiCall';
import { common } from 'src/theme/palette';
import { MAX_BULK_GOOGLE_REVIEW } from 'src/hooks/useGoogleReviewSend';
import { getStatusCellTextColor } from 'src/utils/getClassName';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';

// Quill toolbar config — minimal, clean (mirrors IosEmailComposer)
const QUILL_MODULES = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
    ],
};
const QUILL_FORMATS = ['bold', 'italic', 'underline', 'list', 'bullet', 'link'];

// Hairline row — borderless iOS-style field row
function FieldRow({ label, children, noDivider }) {
    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 0.75, minHeight: 44 }}>
                <Typography
                    variant="body2"
                    sx={{ fontSize: '0.82rem', color: 'text.disabled', fontWeight: 500, minWidth: 60, flexShrink: 0 }}
                >
                    {label}
                </Typography>
                <Box sx={{ flex: 1, ml: 1 }}>{children}</Box>
            </Box>
            {!noDivider && <Divider sx={{ mx: 3, borderColor: 'divider', opacity: 0.6 }} />}
        </>
    );
}
FieldRow.propTypes = { label: PropTypes.string, children: PropTypes.node, noDivider: PropTypes.bool };

// BareInput — styled text input with no border/outline
function BareInput({ value, onChange, placeholder, multiline, inputProps, ...rest }) {
    return (
        <TextField
            variant="standard"
            fullWidth
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            multiline={multiline}
            InputProps={{
                disableUnderline: true,
                inputProps: { style: { fontSize: '0.875rem', padding: 0 }, ...(inputProps || {}) },
            }}
            sx={{ '& .MuiInputBase-root': { p: 0 } }}
            {...rest}
        />
    );
}
BareInput.propTypes = { value: PropTypes.string, onChange: PropTypes.func, placeholder: PropTypes.string, multiline: PropTypes.bool, inputProps: PropTypes.object };

// Minimal fields — enough for display and for building sendRequestBulk entries
const PICKER_FIELDS = 'fields={id,first_name,last_name,lead_contact{email,phone},lead_treatments{id,status,treatment,updated_at},created_at,updated_at}';

const DATE_OPTIONS = [
    { label: '7d', value: '7d' },
    { label: '30d', value: '30d' },
    { label: '90d', value: '90d' },
    { label: 'All', value: 'All' },
];

// All 6 lead statuses — default pre-selects CLOSED + TREATMENT_STARTED
const STATUS_OPTIONS = [
    { label: 'New',          value: 'NEW' },
    { label: 'Booked',       value: 'CONSULTATION_BOOKED' },
    { label: 'Attended',     value: 'CONSULTATION_ATTENDED' },
    { label: 'In Treatment', value: 'TREATMENT_STARTED' },
    { label: 'FTA',          value: 'FAILED_TO_ATTEND_CONSULTATION' },
    { label: 'Completed',    value: 'CLOSED' },
];

const STEP_PICK = 'pick';
const STEP_CONFIRM = 'confirm';
const STEP_RESULTS = 'results';

const getName = (lead) =>
    `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim() || 'Unnamed';

const getInitials = (lead) =>
    `${(lead?.first_name || '?')[0]}${(lead?.last_name || '?')[0]}`.toUpperCase();

/** Build ISO date string for API range param. */
const toRangeParam = (daysBack) => {
    if (!daysBack) return null;
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - daysBack);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return `${fmt(start)} 00:00:00,${fmt(end)} 23:59:00`;
};

const DATE_DAYS = { '7d': 7, '30d': 30, '90d': 90, All: null };

/** Pick best treatment for a lead (latest CLOSED, else latest overall). */
const getBestTreatment = (lead) => {
    const treatments = lead?.lead_treatments || [];
    if (!treatments.length) return null;
    const sorted = [...treatments].sort(
        (a, b) => new Date(b.updated_at) - new Date(a.updated_at)
    );
    return sorted.find((t) => t.status === 'CLOSED') ?? sorted[0];
};

/** 4-tier date color matching CLAUDE.md / dateOfEnquiryLabelStyle logic.
 *  Returns 'text.secondary' for null, undefined, or unparseable date strings. */
const getDateColor = (dateStr) => {
    if (!dateStr) return 'text.secondary';
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return 'text.secondary'; // A2: NaN guard
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays < 1) return common.mintyZest;      // today  → green
    if (diffDays <= 1) return 'text.secondary';      // yesterday → grey
    if (diffDays <= 7) return '#b45309';             // 2–7d → amber
    return '#B71D18';                                 // 8d+ → red
};

/**
 * Dialog for sending Google Review request emails to one or more patients.
 * Three-step flow: pick patients → confirm/compose → show results.
 *
 * B1 — mode toggle: Quick Send (single-patient autocomplete) vs Bulk Send (DataGrid).
 * B2 — two-pane confirm step: recipient list + inline email composer (Editor.jsx).
 * B3 — "Save changes to template" checkbox: PATCH before send, abort on failure.
 * B4 — maxWidth="lg"
 * B5 — "Retry failed (N)" button on results step.
 * B6 — live LinearProgress + "Sending X of Y" during bulk send.
 */
const GoogleReviewSendDialog = ({ open, onClose, practiceId, isSending, onSend, templateData, canEditTemplate, prefilledLead }) => {
    const { enqueueSnackbar } = useCustomSnackbar();
    const [step, setStep] = useState(STEP_PICK);

    // ── B1: Send mode ────────────────────────────────────────────────────────
    const [mode, setMode] = useState('quick');

    // ── Quick-send state ─────────────────────────────────────────────────────
    const [quickSearch, setQuickSearch] = useState('');
    const [quickOptions, setQuickOptions] = useState([]);
    const [quickOptionsLoading, setQuickOptionsLoading] = useState(false);
    // FIX #5: when prefilledLead is provided (e.g. from a checklist cell), pre-select it on open.
    // Existing callers that don't pass prefilledLead see no change (null stays null).
    const [quickSelectedLead, setQuickSelectedLead] = useState(prefilledLead ?? null);

    useEffect(() => {
        if (open && prefilledLead) setQuickSelectedLead(prefilledLead);
    }, [open, prefilledLead]);

    // ── Bulk pick-step state ─────────────────────────────────────────────────
    const [candidates, setCandidates] = useState([]);
    const [candidatesLoading, setCandidatesLoading] = useState(false);
    const [candidateTotal, setCandidateTotal] = useState(0);
    const [fetchError, setFetchError] = useState(null);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(['CLOSED', 'TREATMENT_STARTED']);
    const [dateFilter, setDateFilter] = useState('30d');
    // Treatment filter: array of treatment objects { id, name } from SelectMultiTreatments
    const [treatmentFilter, setTreatmentFilter] = useState([]);

    // Minimal FormProvider so SelectMultiTreatments (uses RHFAutocomplete) doesn't throw
    const treatmentFormMethods = useForm({ defaultValues: { dialogTreatment: [] } });

    // Selection: Map<leadId, leadObject> — persists across page changes
    const [selectedLeadMap, setSelectedLeadMap] = useState(new Map());
    const selectedLeads = useMemo(() => Array.from(selectedLeadMap.values()), [selectedLeadMap]);
    const selectedCount = selectedLeadMap.size;
    const atCap = selectedCount >= MAX_BULK_GOOGLE_REVIEW;
    // IDs of currently selected rows (for DataGrid rowSelectionModel)
    const rowSelectionModel = useMemo(() => Array.from(selectedLeadMap.keys()), [selectedLeadMap]);

    // ── B2: Compose state ────────────────────────────────────────────────────
    const [composeSubject, setComposeSubject] = useState('');
    const [composeBody, setComposeBody] = useState('');
    const hasPopulatedCompose = useRef(false);

    // ── B3: Save-to-template state ───────────────────────────────────────────
    const [saveToTemplate, setSaveToTemplate] = useState(false);
    const [patchError, setPatchError] = useState(null);
    // Tracks whether a PATCH succeeded this session (drives Q3 "saved" banner)
    const [templatePatched, setTemplatePatched] = useState(false);

    // Safeguard: if canEditTemplate flips false (role change / data-load race),
    // silently clear the checkbox so no stale true leaks into a send.
    useEffect(() => {
        if (!canEditTemplate && saveToTemplate) setSaveToTemplate(false);
    }, [canEditTemplate, saveToTemplate]);

    // ── B5/B6: Results + progress ────────────────────────────────────────────
    const [sendOutcomes, setSendOutcomes] = useState(null);
    const [sendProgress, setSendProgress] = useState({ current: 0, total: 0 });

    // ── Track whether smart-defaults load has fired ──────────────────────────
    const initialLoadFired = useRef(false);

    // ── Derived: recipients for the confirm step ─────────────────────────────
    const recipientsForConfirm = useMemo(
        () => (mode === 'quick' && quickSelectedLead ? [quickSelectedLead] : selectedLeads),
        [mode, quickSelectedLead, selectedLeads]
    );
    const recipientCount = recipientsForConfirm.length;

    // ── B1: Quick-send debounced search ──────────────────────────────────────
    // Uses the trigram /leads/search/ endpoint (tolerates typos, name order)
    // Fields must be flat — nested braces break URL parsing on this endpoint
    const debouncedQuickSearch = useCallback(
        debounce(async (q, pid) => {
            if (!q || !pid) { setQuickOptions([]); return; }
            setQuickOptionsLoading(true);
            try {
                const res = await callApi(
                    `/api/v1/practices/${pid}/leads/search/?query=${encodeURIComponent(q)}&limit=25&offset=0&ordering=-created_at&is_archived=false&fields={id,first_name,last_name,lead_contact}`
                );
                if ((res.ok || res.status === 200) && res.data?.results) {
                    setQuickOptions(res.data.results);
                } else {
                    setQuickOptions([]);
                }
            } catch {
                setQuickOptions([]);
            }
            setQuickOptionsLoading(false);
        }, 300),
        []
    );

    // ── Bulk: Fetch candidates ───────────────────────────────────────────────
    const fetchCandidates = useCallback(async (page, pageSize, opts) => {
        if (!practiceId) return;
        setFetchError(null);
        setCandidatesLoading(true);
        try {
            const { status, date, q, treatments } = opts;
            const offset = page * pageSize;
            const statusParam = status.length
                ? `&lead_treatments__status__in=${status.join(',')}`
                : '';
            const range = toRangeParam(DATE_DAYS[date]);
            const dateParam = range ? `&created_at__range=${range}` : '';
            const searchParam = q ? `&search=${encodeURIComponent(q)}` : '';
            const treatmentParam = treatments?.length
                ? `&lead_treatments__treatment__in=${treatments.map((t) => t.id).join(',')}`
                : '';
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/?limit=${pageSize}&offset=${offset}&is_archived=false${statusParam}${dateParam}${searchParam}${treatmentParam}&ordering=-updated_at&${PICKER_FIELDS}`
            );
            if ((res.ok || res.status === 200) && res.data?.results) {
                setCandidates(res.data.results);
                setCandidateTotal(res.data.count ?? 0);
            } else {
                setCandidates([]);
                setCandidateTotal(0);
            }
        } catch (err) {
            setCandidates([]);
            setCandidateTotal(0);
            setFetchError(err?.message || 'Failed to load patients');
        }
        setCandidatesLoading(false);
    }, [practiceId]);

    // Fire smart-defaults bulk load the first time the dialog opens in bulk mode
    const handleDialogOpen = useCallback(() => {
        if (mode !== 'bulk' || initialLoadFired.current) return;
        initialLoadFired.current = true;
        fetchCandidates(0, paginationModel.pageSize, {
            status: ['CLOSED', 'TREATMENT_STARTED'],
            date: '30d',
            q: '',
            treatments: [],
        });
    }, [fetchCandidates, mode, paginationModel.pageSize]);

    // Debounced bulk search
    const debouncedSearch = useCallback(
        debounce((q) => {
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
            fetchCandidates(0, paginationModel.pageSize, {
                status: statusFilter,
                date: dateFilter,
                q,
                treatments: treatmentFilter,
            });
        }, 300),
        [fetchCandidates, statusFilter, dateFilter, paginationModel.pageSize, treatmentFilter]
    );

    // Cancel pending debounce calls on unmount / dep changes to avoid stale state updates
    useEffect(() => () => debouncedQuickSearch.cancel(), [debouncedQuickSearch]);
    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    const handleSearchChange = (e) => {
        const q = e.target.value;
        setSearch(q);
        debouncedSearch(q);
    };

    const handleStatusToggle = (value) => {
        const next = statusFilter.includes(value)
            ? statusFilter.filter((s) => s !== value)
            : [...statusFilter, value];
        setStatusFilter(next);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        fetchCandidates(0, paginationModel.pageSize, {
            status: next,
            date: dateFilter,
            q: search,
            treatments: treatmentFilter,
        });
    };

    const handleDateChange = (value) => {
        setDateFilter(value);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        fetchCandidates(0, paginationModel.pageSize, {
            status: statusFilter,
            date: value,
            q: search,
            treatments: treatmentFilter,
        });
    };

    const handleTreatmentChange = (_, selected) => {
        setTreatmentFilter(selected);
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        fetchCandidates(0, paginationModel.pageSize, {
            status: statusFilter,
            date: dateFilter,
            q: search,
            treatments: selected,
        });
    };

    const handlePaginationModelChange = (model) => {
        setPaginationModel(model);
        fetchCandidates(model.page, model.pageSize, {
            status: statusFilter,
            date: dateFilter,
            q: search,
            treatments: treatmentFilter,
        });
    };

    // DataGrid checkbox selection with cap enforcement
    const handleSelectionChange = useCallback((newIds) => {
        setSelectedLeadMap((prev) => {
            const next = new Map(prev);
            prev.forEach((_, id) => {
                if (!newIds.includes(id)) next.delete(id);
            });
            candidates.forEach((c) => {
                if (newIds.includes(c.id)) next.set(c.id, c);
            });
            if (next.size > MAX_BULK_GOOGLE_REVIEW) {
                const droppedCount = next.size - MAX_BULK_GOOGLE_REVIEW;
                enqueueSnackbar(
                    `Maximum ${MAX_BULK_GOOGLE_REVIEW} patients per send — ${droppedCount} not added.`,
                    { variant: 'warning' }
                );
                const entries = [...next.entries()].slice(0, MAX_BULK_GOOGLE_REVIEW);
                return new Map(entries);
            }
            return next;
        });
    }, [candidates, enqueueSnackbar]);

    // ── B1: Mode switch ──────────────────────────────────────────────────────
    const handleModeChange = (_, val) => {
        if (!val) return;
        setMode(val);
        if (val === 'quick') {
            setSelectedLeadMap(new Map());
        } else {
            setQuickSelectedLead(null);
            setQuickSearch('');
            setQuickOptions([]);
            // Trigger bulk load on first switch to bulk mode
            if (!initialLoadFired.current) {
                initialLoadFired.current = true;
                fetchCandidates(0, paginationModel.pageSize, {
                    status: ['CLOSED', 'TREATMENT_STARTED'],
                    date: '30d',
                    q: '',
                    treatments: [],
                });
            }
        }
    };

    // ── Step transitions ─────────────────────────────────────────────────────
    const handleProceedToConfirm = () => {
        // Pre-populate compose fields from templateData on first transition only
        if (!hasPopulatedCompose.current) {
            setComposeSubject(templateData?.subject || '');
            setComposeBody(templateData?.body || '');
            hasPopulatedCompose.current = true;
        }
        setPatchError(null);
        setStep(STEP_CONFIRM);
    };

    const handleBackToPick = () => {
        setPatchError(null);
        setStep(STEP_PICK);
    };

    // B3: async so we can await the PATCH before sending
    const handleConfirmSend = async (entriesOverride) => {
        const entries = entriesOverride || recipientsForConfirm.map((lead) => {
            const best = getBestTreatment(lead);
            return { leadId: lead.id, leadTreatmentId: best?.id ?? null, leadData: lead };
        });

        // B3: PATCH template first if checkbox is checked; abort on failure
        if (saveToTemplate && canEditTemplate && templateData?.id) {
            setPatchError(null);
            const patchRes = await callApi(
                `/api/v1/practices/${practiceId}/engagement-templates/${templateData.id}/`,
                'patch',
                { subject: composeSubject, body: composeBody }
            );
            if (!patchRes.ok) {
                setPatchError(
                    patchRes.data?.detail ||
                    patchRes.data?.message ||
                    'Failed to save template changes — send aborted.'
                );
                return;
            }
            // PATCH succeeded — record for Q3 results banner
            setTemplatePatched(true);
        }

        // B6: reset progress counter before firing
        setSendProgress({ current: 0, total: entries.length });

        onSend(entries, {
            onProgress: (current, total) => setSendProgress({ current, total }),
            onSuccess: (outcomes) => {
                setSendOutcomes(outcomes);
                setStep(STEP_RESULTS);
            },
            overrides: {
                subject: composeSubject || undefined,
                body: composeBody || undefined,
            },
        });
    };

    // B5: retry only the failed entries, merge new outcomes into existing results
    const handleRetryFailed = () => {
        const failedEntries = (sendOutcomes?.failed || []).map(({ leadData }) => {
            const best = getBestTreatment(leadData);
            return { leadId: leadData.id, leadTreatmentId: best?.id ?? null, leadData };
        });
        setSendProgress({ current: 0, total: failedEntries.length });
        onSend(failedEntries, {
            onProgress: (current, total) => setSendProgress({ current, total }),
            onSuccess: (outcomes) => {
                setSendOutcomes((prev) => ({
                    sent: [...(prev?.sent || []), ...(outcomes.sent || [])],
                    failed: outcomes.failed || [],
                }));
            },
            overrides: {
                subject: composeSubject || undefined,
                body: composeBody || undefined,
            },
        });
    };

    const handleClose = () => {
        if (isSending) return;
        setStep(STEP_PICK);
        setMode('quick');
        // quick
        setQuickSearch('');
        setQuickOptions([]);
        // FIX #5: restore prefilledLead so re-opening pre-selects it again
        setQuickSelectedLead(prefilledLead ?? null);
        // bulk
        setCandidates([]);
        setCandidateTotal(0);
        setPaginationModel({ page: 0, pageSize: 50 });
        setSearch('');
        setStatusFilter(['CLOSED', 'TREATMENT_STARTED']);
        setDateFilter('30d');
        setTreatmentFilter([]);
        treatmentFormMethods.reset({ dialogTreatment: [] });
        setSelectedLeadMap(new Map());
        // compose
        setComposeSubject('');
        setComposeBody('');
        hasPopulatedCompose.current = false;
        setSaveToTemplate(false);
        setPatchError(null);
        setTemplatePatched(false);
        // results / progress
        setSendOutcomes(null);
        setSendProgress({ current: 0, total: 0 });
        initialLoadFired.current = false;
        onClose();
    };

    // ── DataGrid column definitions ──────────────────────────────────────────
    const columns = useMemo(() => [
        {
            field: 'name',
            headerName: 'Patient',
            flex: 1.5,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => {
                const lead = params.row;
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, height: '100%' }}>
                        <Avatar
                            sx={{
                                width: 28,
                                height: 28,
                                fontSize: '11px',
                                fontWeight: 700,
                                bgcolor: alpha('#078DEE', 0.12),
                                color: '#078DEE',
                                flexShrink: 0,
                            }}
                        >
                            {getInitials(lead)}
                        </Avatar>
                        <Typography
                            noWrap
                            sx={{ fontSize: '13px', fontWeight: 600, textTransform: 'capitalize' }}
                        >
                            {getName(lead)}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'contact',
            headerName: 'Contact',
            flex: 1.5,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => {
                const { lead_contact: lc } = params.row;
                const text = lc?.email || lc?.phone || '—';
                const icon = lc?.email ? 'eva:email-fill' : 'eva:phone-fill';
                return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, height: '100%' }}>
                        <Iconify icon={icon} width={14} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                        <Typography noWrap sx={{ fontSize: '12px', color: 'text.secondary' }}>
                            {text}
                        </Typography>
                    </Box>
                );
            },
        },
        {
            field: 'treatment',
            headerName: 'Treatment',
            flex: 1.5,
            minWidth: 140,
            sortable: false,
            renderCell: (params) => {
                const best = getBestTreatment(params.row);
                if (!best) return <Typography sx={{ fontSize: '12px', color: 'text.disabled' }}>—</Typography>;
                const statusColor = getStatusCellTextColor(best.status);
                const STATUS_LABELS = {
                    CLOSED: 'Completed',
                    TREATMENT_STARTED: 'In Treatment',
                };
                const label = STATUS_LABELS[best.status] || best.status;
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 0.25 }}>
                        <Typography noWrap sx={{ fontSize: '12px', fontWeight: 600 }}>
                            {best.treatment || '—'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Box
                                sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: statusColor?.color || 'text.secondary',
                                    flexShrink: 0,
                                }}
                                />
                            <Typography sx={{ fontSize: '11px', color: statusColor?.color || 'text.secondary' }}>
                                {label}
                            </Typography>
                        </Box>
                    </Box>
                );
            },
        },
        {
            field: 'updated_at',
            headerName: 'Last updated',
            width: 110,
            sortable: false,
            renderCell: (params) => {
                const val = params.row.updated_at;
                const color = getDateColor(val);
                // A2: guard against invalid date strings before calling toLocaleDateString
                const d = val ? new Date(val) : null;
                const isValidDate = d && !Number.isNaN(d.getTime());
                const label = isValidDate
                    ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })
                    : '—';
                return (
                    <Typography sx={{ fontSize: '12px', color, fontWeight: 500, height: '100%', display: 'flex', alignItems: 'center' }}>
                        {label}
                    </Typography>
                );
            },
        },
    ], []);

    // ── Shared: recipient list widget ────────────────────────────────────────
    const renderRecipientList = (leads) => (
        <List dense disablePadding>
            {leads.map((lead, i) => (
                <ListItem
                    key={lead.id}
                    divider={i < leads.length - 1}
                    sx={{ py: 0.75, px: 2 }}
                >
                    <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar
                            sx={{
                                width: 28,
                                height: 28,
                                fontSize: '11px',
                                fontWeight: 700,
                                bgcolor: alpha('#078DEE', 0.12),
                                color: '#078DEE',
                            }}
                        >
                            {getInitials(lead)}
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                        primary={getName(lead)}
                        secondary={lead.lead_contact?.email || lead.lead_contact?.phone || ''}
                        primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }}
                        secondaryTypographyProps={{ fontSize: '11px' }}
                    />
                </ListItem>
            ))}
        </List>
    );

    // ── B6: Progress bar widget ───────────────────────────────────────────────
    const renderProgressBar = () => {
        if (!isSending || sendProgress.total === 0) return null;
        const pct = (sendProgress.current / sendProgress.total) * 100;
        return (
            <Box sx={{ px: 2.5, py: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary', fontWeight: 500 }}>
                        Sending…
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                        {sendProgress.current} of {sendProgress.total}
                    </Typography>
                </Box>
                <LinearProgress
                    variant="determinate"
                    value={pct}
                    sx={{
                        borderRadius: 4,
                        height: 6,
                        bgcolor: alpha('#FFAB00', 0.15),
                        '& .MuiLinearProgress-bar': { bgcolor: '#FFAB00', borderRadius: 4 },
                    }}
                />
            </Box>
        );
    };

    // ── Step: Pick — Quick mode ───────────────────────────────────────────────
    const renderQuickPickContent = () => (
        <>
            <DialogContent dividers sx={{ p: 2.5, minHeight: 260 }}>
                <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 2 }}>
                    Search for a patient and send a review request directly.
                </Typography>

                <Autocomplete
                    freeSolo={false}
                    options={quickOptions}
                    loading={quickOptionsLoading}
                    value={quickSelectedLead}
                    onChange={(_, val) => setQuickSelectedLead(val)}
                    inputValue={quickSearch}
                    onInputChange={(_, val, reason) => {
                        setQuickSearch(val);
                        if (reason === 'input') debouncedQuickSearch(val, practiceId);
                    }}
                    getOptionLabel={(opt) => (typeof opt === 'string' ? opt : getName(opt))}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    filterOptions={(x) => x}
                    renderOption={(props, opt) => (
                        <Box component="li" {...props} key={opt.id}>
                            <Avatar
                                sx={{
                                    width: 28,
                                    height: 28,
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    mr: 1.5,
                                    bgcolor: alpha('#078DEE', 0.12),
                                    color: '#078DEE',
                                    flexShrink: 0,
                                }}
                            >
                                {getInitials(opt)}
                            </Avatar>
                            <Box>
                                <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{getName(opt)}</Typography>
                                <Typography sx={{ fontSize: '11px', color: 'text.secondary' }}>
                                    {opt.lead_contact?.email || opt.lead_contact?.phone || ''}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            size="small"
                            placeholder="Search by name, email or phone…"
                            InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                    <>
                                        <InputAdornment position="start">
                                            <Iconify icon="eva:search-fill" width={16} sx={{ color: 'text.disabled' }} />
                                        </InputAdornment>
                                        {params.InputProps.startAdornment}
                                    </>
                                ),
                                endAdornment: (
                                    <>
                                        {quickOptionsLoading && <CircularProgress size={14} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                        />
                    )}
                    PaperComponent={({ children, ...paperProps }) => (
                        <Box
                            {...paperProps}
                            sx={{
                                bgcolor: 'background.paper',
                                borderRadius: '12px',
                                boxShadow: '0px 20px 40px -4px rgba(145,158,171,0.24)',
                                border: '1px solid',
                                borderColor: 'divider',
                                mt: 0.5,
                            }}
                        >
                            {children}
                        </Box>
                    )}
                />

                {quickSelectedLead && (
                    <Box
                        sx={{
                            mt: 2,
                            p: 1.5,
                            borderRadius: '10px',
                            border: '1px solid',
                            borderColor: alpha(common.mintyZest, 0.4),
                            bgcolor: alpha(common.mintyZest, 0.06),
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                        }}
                    >
                        <Avatar
                            sx={{
                                width: 36,
                                height: 36,
                                fontSize: '13px',
                                fontWeight: 700,
                                bgcolor: alpha('#078DEE', 0.12),
                                color: '#078DEE',
                                flexShrink: 0,
                            }}
                        >
                            {getInitials(quickSelectedLead)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a1a' }}>
                                {getName(quickSelectedLead)}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                                {quickSelectedLead.lead_contact?.email || quickSelectedLead.lead_contact?.phone || ''}
                            </Typography>
                        </Box>
                        <Iconify icon="mdi:check-circle" width={20} sx={{ color: common.mintyZest, flexShrink: 0 }} />
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: '#666', borderRadius: '8px' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={!quickSelectedLead}
                    onClick={handleProceedToConfirm}
                    startIcon={<Iconify icon="solar:star-bold-duotone" width={16} />}
                    sx={{
                        bgcolor: '#FFAB00',
                        fontWeight: 600,
                        fontSize: '13px',
                        borderRadius: '10px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#B76E00' },
                        '&.Mui-disabled': { bgcolor: alpha('#FFAB00', 0.5), color: '#fff' },
                    }}
                >
                    Continue
                </Button>
            </DialogActions>
        </>
    );

    // ── Step: Pick — Bulk mode ────────────────────────────────────────────────
    const renderBulkPickContent = () => (
        <>
            <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                {/* Filter bar */}
                <Box sx={{ px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <TextField
                        fullWidth
                        size="small"
                        value={search}
                        onChange={handleSearchChange}
                        placeholder="Search by name, email or phone…"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Iconify icon="eva:search-fill" width={16} sx={{ color: 'text.disabled' }} />
                                </InputAdornment>
                            ),
                        }}
                        sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />

                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap alignItems="center">
                        {/* A4: all 6 lead statuses */}
                        {STATUS_OPTIONS.map(({ label, value }) => {
                            const active = statusFilter.includes(value);
                            return (
                                <Chip
                                    key={value}
                                    label={label}
                                    size="small"
                                    onClick={() => handleStatusToggle(value)}
                                    sx={{
                                        fontSize: '12px',
                                        height: 26,
                                        bgcolor: active ? alpha(common.mintyZest, 0.12) : 'transparent',
                                        color: active ? common.mintyZest : 'text.secondary',
                                        border: '1px solid',
                                        borderColor: active ? alpha(common.mintyZest, 0.4) : 'divider',
                                        fontWeight: active ? 600 : 400,
                                        '&:hover': { bgcolor: alpha(common.mintyZest, 0.08) },
                                    }}
                                />
                            );
                        })}

                        {/* A3: proper MUI vertical divider between chip groups */}
                        <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 0.5 }} />

                        {DATE_OPTIONS.map(({ label, value }) => {
                            const active = dateFilter === value;
                            return (
                                <Chip
                                    key={value}
                                    label={label}
                                    size="small"
                                    onClick={() => handleDateChange(value)}
                                    sx={{
                                        fontSize: '12px',
                                        height: 26,
                                        bgcolor: active ? alpha('#078DEE', 0.12) : 'transparent',
                                        color: active ? '#078DEE' : 'text.secondary',
                                        border: '1px solid',
                                        borderColor: active ? alpha('#078DEE', 0.4) : 'divider',
                                        fontWeight: active ? 600 : 400,
                                        '&:hover': { bgcolor: alpha('#078DEE', 0.08) },
                                    }}
                                />
                            );
                        })}
                    </Stack>

                    {/* A5: Treatment multi-select filter */}
                    <FormProvider {...treatmentFormMethods}>
                        <Box sx={{ mt: 1.25 }}>
                            <SelectMultiTreatments
                                name="dialogTreatment"
                                type="NewLeadTable"
                                value={treatmentFilter}
                                onChange={handleTreatmentChange}
                                size="small"
                                label="Treatment (all)"
                                placeholder="Filter by treatment…"
                            />
                        </Box>
                    </FormProvider>
                </Box>

                {/* Cap banner */}
                {atCap && (
                    <Box
                        sx={{
                            px: 2, py: 0.75,
                            display: 'flex', alignItems: 'center', gap: 1,
                            bgcolor: alpha('#FFAB00', 0.08),
                            borderBottom: '1px solid',
                            borderColor: alpha('#FFAB00', 0.25),
                        }}
                    >
                        <Iconify icon="mdi:alert-outline" width={15} sx={{ color: '#FFAB00', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '12px', color: '#7A4100' }}>
                            Limit reached — {selectedCount} of {candidateTotal} patients selected (max {MAX_BULK_GOOGLE_REVIEW})
                        </Typography>
                    </Box>
                )}

                {/* DataGrid — A1: pageSizeOptions has 3 values so footer renders */}
                <Box sx={{ flex: 1, minHeight: 0, height: 380 }}>
                    <DataGridPro
                        rows={candidates}
                        columns={columns}
                        loading={candidatesLoading}
                        checkboxSelection
                        disableColumnMenu
                        disableColumnResize
                        rowSelectionModel={rowSelectionModel}
                        onRowSelectionModelChange={handleSelectionChange}
                        keepNonExistentRowsSelected
                        pagination
                        paginationMode="server"
                        paginationModel={paginationModel}
                        onPaginationModelChange={handlePaginationModelChange}
                        rowCount={candidateTotal}
                        pageSizeOptions={[25, 50, 100]}
                        density="compact"
                        getRowId={(row) => row.id}
                        isRowSelectable={(params) => !atCap || rowSelectionModel.includes(params.id)}
                        sx={{
                            border: 'none',
                            '& .MuiDataGrid-columnHeaders': {
                                bgcolor: '#F4F6F8',
                                fontSize: '12px',
                                fontWeight: 600,
                                minHeight: '36px !important',
                                maxHeight: '36px !important',
                            },
                            '& .MuiDataGrid-row': { fontSize: '13px' },
                            '& .MuiDataGrid-cell': { borderColor: 'divider' },
                            '& .MuiDataGrid-footerContainer': {
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                minHeight: 40,
                            },
                            '& .MuiDataGrid-overlay': { bgcolor: 'transparent' },
                        }}
                        slots={{
                            noRowsOverlay: () => {
                                if (fetchError) {
                                    return (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
                                            <Iconify icon="mdi:alert-circle-outline" width={32} sx={{ color: '#FF5630' }} />
                                            <Typography sx={{ fontSize: '13px', color: '#637381' }}>
                                                Failed to load patients — try again
                                            </Typography>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => fetchCandidates(paginationModel.page, paginationModel.pageSize, { status: statusFilter, date: dateFilter, q: search, treatments: treatmentFilter })}
                                                sx={{ fontSize: '12px', borderRadius: '8px', mt: 0.5 }}
                                            >
                                                Retry
                                            </Button>
                                        </Box>
                                    );
                                }
                                return (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 1 }}>
                                        <Iconify icon="solar:star-bold-duotone" width={32} sx={{ color: '#ccc' }} />
                                        <Typography sx={{ fontSize: '13px', color: '#888' }}>
                                            {statusFilter.length === 0
                                                ? 'Select at least one status filter above'
                                                : 'No patients match your filters'}
                                        </Typography>
                                    </Box>
                                );
                            },
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                    {selectedCount > 0 && (
                        <Typography sx={{ fontSize: '12px', color: 'text.secondary' }}>
                            {selectedCount} patient{selectedCount !== 1 ? 's' : ''} selected
                            {atCap && (
                                <Typography component="span" sx={{ fontSize: '12px', color: '#FFAB00', ml: 0.5 }}>
                                    — max reached
                                </Typography>
                            )}
                        </Typography>
                    )}
                </Box>
                <Button onClick={handleClose} sx={{ color: '#666', borderRadius: '8px' }}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disabled={selectedCount === 0}
                    onClick={handleProceedToConfirm}
                    startIcon={<Iconify icon="solar:star-bold-duotone" width={16} />}
                    sx={{
                        bgcolor: '#FFAB00',
                        fontWeight: 600,
                        fontSize: '13px',
                        borderRadius: '10px',
                        px: 2.5,
                        '&:hover': { bgcolor: '#B76E00' },
                        '&.Mui-disabled': { bgcolor: alpha('#FFAB00', 0.5), color: '#fff' },
                    }}
                >
                    {`Continue (${selectedCount})`}
                </Button>
            </DialogActions>
        </>
    );

    // ── Step: Pick (B1 mode-toggle wrapper) ──────────────────────────────────
    const renderPickStep = () => (
        <>
            {/* B1: Mode toggle */}
            <Box
                sx={{
                    px: 2.5,
                    pt: 1.5,
                    pb: 1.5,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    flexShrink: 0,
                }}
            >
                <ToggleButtonGroup
                    value={mode}
                    exclusive
                    onChange={handleModeChange}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            fontSize: '12px',
                            fontWeight: 600,
                            px: 2,
                            py: 0.6,
                            border: '1px solid !important',
                            borderColor: 'divider !important',
                            borderRadius: '8px !important',
                            mr: 0.75,
                            textTransform: 'none',
                            color: 'text.secondary',
                            '&.Mui-selected': {
                                bgcolor: alpha('#FFAB00', 0.1),
                                color: '#B76E00',
                                borderColor: `${alpha('#FFAB00', 0.4)} !important`,
                            },
                        },
                    }}
                >
                    <ToggleButton value="quick">
                        <Iconify icon="mdi:lightning-bolt" width={14} sx={{ mr: 0.5 }} />
                        Quick Send
                    </ToggleButton>
                    <ToggleButton value="bulk">
                        <Iconify icon="mdi:format-list-bulleted" width={14} sx={{ mr: 0.5 }} />
                        Bulk Send
                    </ToggleButton>
                </ToggleButtonGroup>
                <Typography sx={{ fontSize: '11px', color: 'text.disabled' }}>
                    {mode === 'quick'
                        ? 'Send to one patient'
                        : `Send to up to ${MAX_BULK_GOOGLE_REVIEW} patients at once`}
                </Typography>
            </Box>

            {mode === 'quick' ? renderQuickPickContent() : renderBulkPickContent()}
        </>
    );

    // ── Step: Confirm — iOS email composer aesthetic ───────────────────────────────────────────────
    const isUnlayerTemplate = Boolean(templateData?.use_unlayer);

    const renderConfirmStep = () => (
        <>
            {/* Two-pane layout: recipient list (left) + compose (right) */}
            <DialogContent
                sx={{
                    p: 0,
                    display: 'flex',
                    minHeight: 0,
                    height: '62vh',
                    overflow: 'hidden',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    '&.MuiDialogContent-dividers': { borderBottom: 'none' },
                }}
            >
                {/* Left pane: recipient list — EmailTemplateSidePanel aesthetic */}
                <Box
                    sx={{
                        width: '30%',
                        flexShrink: 0,
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        bgcolor: (theme) => alpha(theme.palette.grey[100], 0.5),
                    }}
                >
                    <Box sx={{ px: 1.5, pt: 1.5, pb: 1, flexShrink: 0 }}>
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontSize: '0.78rem',
                                fontWeight: 700,
                                color: 'text.secondary',
                                textTransform: 'uppercase',
                                letterSpacing: '0.06em',
                            }}
                        >
                            Recipients ({recipientCount})
                        </Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ flex: 1, overflowY: 'auto' }}>
                        {renderRecipientList(recipientsForConfirm)}
                    </Box>
                </Box>

                {/* Right pane: iOS-style compose */}
                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minWidth: 0 }}>
                    {/* iOS header: Back | "Preview & Send" / recipient count | round send button */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            pt: 1,
                            pb: 0.5,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            flexShrink: 0,
                        }}
                    >
                        <Button
                            size="small"
                            onClick={handleBackToPick}
                            disabled={isSending}
                            sx={{ fontSize: '0.875rem', color: 'primary.main', textTransform: 'none', fontWeight: 400, minWidth: 0, px: 1 }}
                        >
                            Back
                        </Button>

                        <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                                Preview &amp; Send
                            </Typography>
                            <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary', display: 'block', lineHeight: 1.2 }}>
                                {`Sending to ${recipientCount} patient${recipientCount !== 1 ? 's' : ''}`}
                            </Typography>
                        </Box>

                        <Tooltip title={isSending ? 'Sending…' : `Send to ${recipientCount} patient${recipientCount !== 1 ? 's' : ''}`}>
                            <span>
                                <IconButton
                                    onClick={() => handleConfirmSend()}
                                    disabled={isSending}
                                    sx={{
                                        bgcolor: isSending ? 'action.disabledBackground' : 'primary.main',
                                        color: isSending ? 'text.disabled' : '#fff',
                                        width: 32,
                                        height: 32,
                                        borderRadius: '50%',
                                        '&:hover': { bgcolor: 'primary.dark' },
                                        transition: 'background-color 200ms ease',
                                    }}
                                >
                                    {isSending
                                        ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                                        : <Iconify icon="eva:paper-plane-fill" width={15} />
                                    }
                                </IconButton>
                            </span>
                        </Tooltip>
                    </Box>

                    {/* Field rows + body */}
                    <Box sx={{ overflowY: 'auto', flex: 1 }}>
                        <FieldRow label="Subject">
                            <BareInput
                                value={composeSubject}
                                onChange={setComposeSubject}
                                placeholder="Subject"
                                inputProps={{ maxLength: 255 }}
                            />
                        </FieldRow>

                        {/* Body — ReactQuill (or read-only Unlayer fallback) */}
                        {isUnlayerTemplate ? (
                            <Box sx={{ px: 3, pt: 1, pb: 2, minHeight: 200, bgcolor: (theme) => alpha(theme.palette.grey[100], 0.4), overflowY: 'auto' }}>
                                <Typography sx={{ fontSize: '0.72rem', color: 'text.disabled', mb: 1, fontStyle: 'italic' }}>
                                    This template uses the advanced editor — body is read-only here.
                                </Typography>
                                {/* DOMPurify sanitizes before render — safe */}
                                {/* eslint-disable-next-line react/no-danger */}
                                <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(templateData?.body || '') }} />
                            </Box>
                        ) : (
                            <Box
                                sx={{
                                    px: 3,
                                    pt: 1,
                                    pb: 2,
                                    minHeight: 220,
                                    '& .ql-toolbar': {
                                        border: 'none',
                                        borderBottom: '1px solid',
                                        borderColor: 'divider',
                                        px: 0,
                                    },
                                    '& .ql-container': {
                                        border: 'none',
                                        fontSize: '0.875rem',
                                        fontFamily: 'inherit',
                                    },
                                    '& .ql-editor': {
                                        minHeight: 200,
                                        p: '8px 0',
                                        '&.ql-blank::before': { fontStyle: 'normal', color: 'rgba(145,158,171,0.8)', left: 0 },
                                    },
                                }}
                            >
                                <ReactQuill
                                    theme="snow"
                                    value={composeBody}
                                    onChange={setComposeBody}
                                    modules={QUILL_MODULES}
                                    formats={QUILL_FORMATS}
                                    placeholder="Write your message…"
                                />
                            </Box>
                        )}
                    </Box>

                    {/* B3: Save-to-template checkbox — hidden for USER role (only ADMIN/TCO/superuser) */}
                    {canEditTemplate && (
                        <Box
                            sx={{
                                px: 2,
                                py: 1,
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                bgcolor: (theme) => alpha(theme.palette.grey[100], 0.6),
                                flexShrink: 0,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={saveToTemplate}
                                        onChange={(e) => setSaveToTemplate(e.target.checked)}
                                        sx={{ color: 'text.secondary', '&.Mui-checked': { color: 'primary.main' } }}
                                    />
                                }
                                label={
                                    <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                        Save changes to the template for future sends
                                        {recipientCount > 1 && saveToTemplate && (
                                            <Typography
                                                component="span"
                                                sx={{ ml: 0.75, fontSize: '0.72rem', color: 'primary.main', fontWeight: 600 }}
                                            >
                                                — edits apply to all {recipientCount} recipients
                                            </Typography>
                                        )}
                                    </Typography>
                                }
                            />
                        </Box>
                    )}
                </Box>
            </DialogContent>

            {/* B3: PATCH error notice */}
            {patchError && (
                <Box
                    sx={{
                        px: 2.5,
                        py: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        bgcolor: alpha('#FF5630', 0.07),
                        borderTop: '1px solid',
                        borderColor: alpha('#FF5630', 0.3),
                    }}
                >
                    <Iconify icon="mdi:alert-circle-outline" width={15} sx={{ color: '#B71D18', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '12px', color: '#B71D18' }}>
                        {patchError}
                    </Typography>
                </Box>
            )}

            {/* B6: progress bar */}
            {renderProgressBar()}
        </>
    );

    // ── Step: Results ─────────────────────────────────────────────────────────
    const renderResultsStep = () => {
        const sentCount = sendOutcomes?.sent?.length ?? 0;
        const failedLeads = sendOutcomes?.failed ?? [];
        const failedCount = failedLeads.length;
        const allOk = failedCount === 0;

        return (
            <>
                <DialogContent dividers sx={{ pt: 3, pb: 3 }}>
                    {/* Q3: "Template saved" notice — shown if PATCH ran this session */}
                    {templatePatched && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.75,
                                px: 1.5,
                                py: 0.75,
                                mb: 2,
                                borderRadius: '8px',
                                bgcolor: alpha('#00AB55', 0.08),
                                border: '1px solid',
                                borderColor: alpha('#00AB55', 0.25),
                            }}
                        >
                            <Iconify icon="mdi:check-circle" width={15} sx={{ color: '#00AB55', flexShrink: 0 }} />
                            <Typography sx={{ fontSize: '12px', color: '#00AB55', fontWeight: 600 }}>
                                Template changes saved
                            </Typography>
                        </Box>
                    )}
                    <Box sx={{ textAlign: 'center', mb: 2 }}>
                        <Box
                            sx={{
                                width: 56,
                                height: 56,
                                borderRadius: '16px',
                                bgcolor: allOk ? alpha('#00AB55', 0.12) : alpha('#FF5630', 0.08),
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                mx: 'auto',
                                mb: 1.5,
                            }}
                        >
                            <Iconify
                                icon={allOk ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'}
                                width={28}
                                sx={{ color: allOk ? '#00AB55' : '#FF5630' }}
                            />
                        </Box>
                        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a' }}>
                            {allOk ? 'All sent successfully' : 'Partially sent'}
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: '#666', mt: 0.5 }}>
                            {allOk
                                ? `Review request${sentCount !== 1 ? 's' : ''} sent to ${sentCount} patient${sentCount !== 1 ? 's' : ''}.`
                                : `Sent to ${sentCount} of ${sentCount + failedCount} patients. ${failedCount} failed.`}
                        </Typography>
                    </Box>

                    {/* Failed list */}
                    {failedCount > 0 && (
                        <Box
                            sx={{
                                maxHeight: 200,
                                overflowY: 'auto',
                                borderRadius: '10px',
                                border: '1px solid',
                                borderColor: alpha('#FF5630', 0.3),
                                bgcolor: alpha('#FF5630', 0.03),
                            }}
                        >
                            <Box sx={{ px: 2, py: 1, borderBottom: '1px solid', borderColor: alpha('#FF5630', 0.15) }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#B71D18' }}>
                                    Failed to send ({failedCount})
                                </Typography>
                            </Box>
                            <List dense disablePadding>
                                {failedLeads.map(({ leadData, error }, i) => (
                                    <ListItem
                                        key={leadData?.id ?? i}
                                        divider={i < failedLeads.length - 1}
                                        sx={{ py: 0.75, px: 2 }}
                                    >
                                        <ListItemAvatar sx={{ minWidth: 40 }}>
                                            <Avatar
                                                sx={{
                                                    width: 28,
                                                    height: 28,
                                                    fontSize: '11px',
                                                    fontWeight: 700,
                                                    bgcolor: alpha('#FF5630', 0.12),
                                                    color: '#B71D18',
                                                }}
                                            >
                                                {getInitials(leadData)}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText
                                            primary={getName(leadData)}
                                            secondary={error || 'Request failed'}
                                            primaryTypographyProps={{ fontSize: '13px', fontWeight: 600 }}
                                            secondaryTypographyProps={{ fontSize: '11px', color: '#B71D18' }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    )}
                </DialogContent>

                {/* B6: progress bar shown during retry */}
                {renderProgressBar()}

                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    {/* B5: retry failed button */}
                    {failedCount > 0 && (
                        <Tooltip title={isSending ? 'Send in progress…' : ''} placement="top">
                            <span>
                                <Button
                                    variant="outlined"
                                    disabled={isSending}
                                    onClick={handleRetryFailed}
                                    startIcon={<Iconify icon="mdi:refresh" width={16} />}
                                    sx={{
                                        color: '#B71D18',
                                        borderColor: alpha('#B71D18', 0.5),
                                        borderRadius: '10px',
                                        '&:hover': { borderColor: '#B71D18', bgcolor: alpha('#B71D18', 0.04) },
                                        '&.Mui-disabled': { color: alpha('#B71D18', 0.4), borderColor: alpha('#B71D18', 0.2) },
                                    }}
                                >
                                    Retry failed ({failedCount})
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button
                        variant="contained"
                        onClick={handleClose}
                        sx={{
                            bgcolor: '#00AB55',
                            fontWeight: 600,
                            fontSize: '13px',
                            borderRadius: '10px',
                            px: 3,
                            '&:hover': { bgcolor: '#007B55' },
                        }}
                    >
                        Done
                    </Button>
                </DialogActions>
            </>
        );
    };

    // ── Step indicator metadata ───────────────────────────────────────────────
    const stepMeta = {
        [STEP_PICK]: {
            icon: 'solar:star-bold-duotone',
            title: 'Send Review Requests',
            subtitle: mode === 'quick'
                ? 'Find a patient and send them a review request'
                : 'Filter and select patients, then send all at once',
        },
        [STEP_CONFIRM]: {
            icon: 'mdi:send',
            title: 'Preview & Send',
            subtitle: `Editing email for ${recipientCount} patient${recipientCount !== 1 ? 's' : ''}`,
        },
        [STEP_RESULTS]: {
            icon:
                sendOutcomes?.failed?.length === 0
                    ? 'mdi:check-circle-outline'
                    : 'mdi:alert-circle-outline',
            title: 'Send Complete',
            subtitle: 'Review the results below',
        },
    };

    const currentMeta = stepMeta[step];

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="lg"
            fullWidth
            TransitionComponent={Grow}
            TransitionProps={{ timeout: 180, onEntered: handleDialogOpen }}
            BackdropProps={{ sx: { backdropFilter: 'blur(4px)', backgroundColor: 'rgba(0,0,0,0.32)' } }}
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    // eslint-disable-next-line no-nested-ternary
                    height: step === STEP_CONFIRM ? '88vh' : step === STEP_PICK ? '90vh' : 'auto',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column',
                },
            }}
        >
            <DialogTitle sx={{ pb: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: '10px',
                            bgcolor: alpha('#FFAB00', 0.12),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                        }}
                    >
                        <Iconify icon={currentMeta.icon} width={20} sx={{ color: '#FFAB00' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: '16px', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.3 }}>
                            {currentMeta.title}
                        </Typography>
                        <Typography sx={{ fontSize: '12px', color: '#888' }}>
                            {currentMeta.subtitle}
                        </Typography>
                    </Box>
                    {/* Step indicator pills */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                        {[STEP_PICK, STEP_CONFIRM, STEP_RESULTS].map((s) => (
                            <Box
                                key={s}
                                sx={{
                                    width: s === step ? 16 : 6,
                                    height: 6,
                                    borderRadius: '3px',
                                    bgcolor: s === step ? '#FFAB00' : alpha('#FFAB00', 0.25),
                                    transition: 'width 0.2s',
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            </DialogTitle>

            {step === STEP_PICK && renderPickStep()}
            {step === STEP_CONFIRM && renderConfirmStep()}
            {step === STEP_RESULTS && renderResultsStep()}
        </Dialog>
    );
};

GoogleReviewSendDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isSending: PropTypes.bool,
    onSend: PropTypes.func.isRequired,
    templateData: PropTypes.object,
    /** Only ADMIN, TCO, and superusers may PATCH the global template (B3 checkbox).
     *  Defaults to false — fail-closed if prop is not passed or not yet loaded. */
    canEditTemplate: PropTypes.bool,
    /** When provided, pre-selects this lead in Quick Send mode on open.
     *  Existing callers that don't pass this see no change. */
    prefilledLead: PropTypes.object,
};

GoogleReviewSendDialog.defaultProps = {
    practiceId: null,
    isSending: false,
    templateData: null,
    canEditTemplate: false,
    prefilledLead: null,
};

export default GoogleReviewSendDialog;
